export interface PHIValue<T> {
  isPHI: true;
  toString: () => string;
  valueOf: () => T;
  toJSON: () => string;
  carefullyAccessPHIValue: T;
}

export const REDACTED_PHI = "[REDACTED_PHI]";

/**
 * Helper to wrap a value in a PHIValue object.
 *
 * This should be used anywhere that we instantiate a PHI value,
 * and want to pass it around without exposing the PHI value.
 */
export function createPHIValue<T>(rawPHIValue: T): PHIValue<T> {
  const phiObject = {
    isPHI: true as const,

    /**
     * This covers if it is put into a string.
     * e.g. `Hello ${value}` produces "Hello [REDACTED_PHI]"
     *
     * @returns "[REDACTED_PHI]"
     */
    toString() {
      return REDACTED_PHI;
    },

    /**
     * Throws an error when trying to access the value in unsafe way
     */
    valueOf() {
      throw new Error("PHIValue cannot be accessed directly");
    },

    /**
     * This covers if we try to stringify within JSON.
     * e.g. JSON.stringify({ hello: "world", value }) produces '{"hello":"world","value":"[REDACTED_PHI]"}'
     *
     * @returns "[REDACTED_PHI]"
     */
    toJSON() {
      return REDACTED_PHI;
    },

    /**
     * This is the only correct way to access the value.
     * e.g. `value.carefullyAccessPHIValue` return the true value of PHIValueObject
     */
    get carefullyAccessPHIValue() {
      return rawPHIValue;
    },
  };

  return phiObject;
}

export function isPHIValue<T>(value: unknown): value is PHIValue<T> {
  return (
    typeof value === "object" &&
    value !== null &&
    "isPHI" in value &&
    (value as { isPHI: unknown }).isPHI === true
  );
}
